import React, { useEffect, useState, useRef } from 'react';
import { getAccessorialRequestRule } from 'utils/UserServiceAPIUtils';
import Spinner from 'components/ui/Spinner';
import Label from 'components/v2/forms/Label';
import PropTypes from 'prop-types';
import { isEqual, sortBy } from 'lodash';
import RequiredAccessorialDocumentList from 'components/modules/shipments/accessorials/RequiredAccessorialDocumentsList';
import { t } from 'i18next';
import { getDocuments } from 'utils/DocumentsAPIUtils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { accessorialRequestDocsWhitelist, OTHER } from 'config/documents';
// Remove when SSR goes live
import {
  carrierAccessorialDocumentsMap,
  CUSTOMER_APPROVAL,
  hiddenFromCarrierMap,
} from 'config/documents';
import SessionStore from 'stores/SessionStore';
import {
  getUniqueRequiredAccessorialDocuments,
  formatBackupDocuments,
} from './utils';
import { FEATURE_FLAG_ENABLED_BREADCRUMB } from 'constants/temp-feature-flag-breadcrumb';
import { fetchAccessorials } from 'components/modules/carrier/modals/helpers/accessorial-rate-helpers';
import { DOCUMENT_STATUSES } from 'components/modules/documents/Document';

const ViewAccessorialRequestDocumentRequirements = (props) => {
  const {
    accessorial,
    shipper,
    shipment,
    handleDocumentUploading,
    containerClassName,
    handleCustomerApprovalChanges,
    handleUpdateAccessorialRequiredDocs,
    dataTestId,
    setIsCarrierMissingRequiredAccDocuments,
    hasGenericSupportingDocOnly,
    setHasGenericSupportingDocOnly,
    isExistingAccessorialRequest,
  } = props;

  const [requiredDocs, setRequiredDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [currentDocs, setCurrentDocs] = useState([]);
  const [accessorialTypeId, setAccessorialTypeId] = useState(null);
  const accessorialTypeSlug = accessorial?.slug ?? null;

  useEffect(() => {
    if (FEATURE_FLAG_ENABLED_BREADCRUMB && isExistingAccessorialRequest) {
      const fetchAccessorialData = async () => {
        try {
          if (accessorialTypeSlug) {
            const accessorialQuery = { slug: accessorialTypeSlug, limit: 1 };
            const accessorialResult = await fetchAccessorials(accessorialQuery);
            if (accessorialResult?.length) {
              setAccessorialTypeId(accessorialResult[0].id);
            } else {
              setAccessorialTypeId(null);
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
      fetchAccessorialData();
    }
    if (FEATURE_FLAG_ENABLED_BREADCRUMB && !isExistingAccessorialRequest) {
      setAccessorialTypeId(accessorial?.id);
    }
  }, [accessorial?.id, accessorialTypeSlug]);

  function renderSpinner() {
    return (
      <div data-testid="div-913dc9c4" className="ml-4 w-12">
        <Spinner
          dataTestId="Spinner-e8154961"
          showMessage
          message={t('LOADING')}
        />
      </div>
    );
  }

  function usePreviousProps(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const validateRequiredFilesUploaded = ({ currentDocs, requiredDocs }) => {
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      const isCarrier = SessionStore.isCarrier();
      if (isCarrier) {
        if (requiredDocs.length) {
          for (const requiredDoc of requiredDocs) {
            if (hiddenFromCarrierMap[requiredDoc.value]) {
              // Customer approval document is for shippers to upload NOT for carriers to upload
              continue;
            }
            const doc = currentDocs.find((currentDoc) => {
              if (accessorialRequestDocsWhitelist[currentDoc.type]) {
                return currentDoc.type === requiredDoc.value;
              }
              return (
                currentDoc.type === requiredDoc.value &&
                currentDoc.foreignKeys.includes(accessorialTypeId)
              );
            });
            if (!doc) {
              setIsCarrierMissingRequiredAccDocuments(true);
              return;
            }
          }
        }
      }
      setIsCarrierMissingRequiredAccDocuments(false);
    }
  };

  // Prevent document uploader from constantly refreshing
  const previousAccessorial = usePreviousProps(accessorial);
  useEffect(() => {
    if (!isEqual(previousAccessorial, accessorial)) {
      getDocs();
    }
  }, [accessorial]);

  useEffect(() => {
    checkCustomerApproval();
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      validateRequiredFilesUploaded({ currentDocs, requiredDocs });
    }
  }, [currentDocs, requiredDocs, files]);

  const getDocs = async () => {
    setIsLoading(true);

    /* Need to set this to isProd check after testing is complete */
    if (!featureEnabled(CONFIGURED_FEATURES.SSR)) {
      /*
          Billing Team Request - Remove this once SSR goes live with customer approval
          Users would like a place to upload a customer approval document, it needs to be not viewable by carriers
          We're putting it in the panel since this is where it'll go once SSR goes live
        */
      setRequiredDocs([{ value: CUSTOMER_APPROVAL.value }]);
    } else {
      handleUpdateAccessorialRequiredDocs(false);
      await getAndSetRequiredDocuments();
    }
    await getAndSetCurrentDocuments();
    setIsLoading(false);
    handleUpdateAccessorialRequiredDocs(true);
  };

  const getForeignKeys = (shipment) => {
    const keys = [
      shipment._id,
      ...getPayoutIds(shipment),
      ...getInvoiceIds(shipment),
    ];

    return [...new Set(keys)].filter((key) => key);
  };

  const getAndSetCurrentDocuments = async () => {
    const foreignKeys = getForeignKeys(shipment);
    const getDocumentsResponse = await getDocuments(foreignKeys);
    const documentsData =
      getDocumentsResponse.data?.getDocuments.documents ?? [];

    const mappedDocumentsData = documentsData
      .filter((document) => document.status === DOCUMENT_STATUSES.UPLOADED)
      .map((document) => {
        return {
          ...document,
        };
      });

    setCurrentDocs(mappedDocumentsData);
  };

  const getPayoutIds = (shipment) => {
    return shipment?.payables?.map((payable) => payable?.payout_id) || [];
  };

  const getInvoiceIds = (shipment) => {
    return (
      shipment?.receivables?.map((receivable) => receivable?.invoice_id) || []
    );
  };

  const getAndSetRequiredDocuments = async () => {
    const docs = await getRequiredAccessorialDocuments(accessorial, shipper);
    if (docs) {
      setRequiredDocs(docs);
      setFiles([]);
    }
  };

  const checkCustomerApproval = () => {
    const customerApprovalRequired = requiredDocs.some(
      (doc) => doc?.value === CUSTOMER_APPROVAL.value,
    );
    const customerApprovalPreviouslyUploaded = currentDocs.some(
      (doc) => doc?.type === CUSTOMER_APPROVAL.value,
    );
    const customerApprovalUploaded = files.some(
      (doc) => doc?.type === CUSTOMER_APPROVAL.value,
    );
    const customerApprovalProvided =
      customerApprovalUploaded || customerApprovalPreviouslyUploaded;
    handleCustomerApprovalChanges(
      customerApprovalRequired,
      customerApprovalProvided,
    );
  };

  const getRequiredAccessorialDocuments = async (accessorial, shipper) => {
    try {
      const accessorialSlug = accessorial?.slug ?? null;
      const userResponse = await getAccessorialRequestRule(shipper, {
        count: false,
        sort: 'ASC',
        page: 1,
        per_page: 20,
        accessorials: accessorialSlug ? [accessorial.slug] : [],
      });
      const carrierDocumentsMapResults =
        carrierAccessorialDocumentsMap[accessorialSlug] ?? [];

      if (!userResponse) {
        throw new Error('No response from API');
      }
      const accessorialData =
        userResponse.data?.getAccessorialRequestRule?.items[0];

      const accessorialDocsRules = accessorialData?.rules || [];

      let accessorialDocsRequired = getUniqueRequiredAccessorialDocuments(
        accessorialDocsRules,
        carrierDocumentsMapResults,
      );

      const visibleToCarrierDocs = filterHiddenFromCarrierDocs(
        accessorialDocsRequired,
      );

      if (SessionStore.isCarrier()) {
        accessorialDocsRequired = visibleToCarrierDocs;
      }

      if (
        FEATURE_FLAG_ENABLED_BREADCRUMB &&
        (!accessorialDocsRequired.length || !visibleToCarrierDocs.length) &&
        accessorial?.slug
      ) {
        setHasGenericSupportingDocOnly(true);

        accessorialDocsRequired.push(OTHER);
      } else if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
        setHasGenericSupportingDocOnly(false);
      }

      if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
        accessorialDocsRequired = formatBackupDocuments(
          accessorial.name,
          accessorial.slug,
          accessorialDocsRequired,
        );
      }

      if (accessorialData?.customer_approval_required) {
        accessorialDocsRequired.push({
          value: CUSTOMER_APPROVAL.value,
          file: null,
        });
      }

      return sortBy(accessorialDocsRequired, ['value']);
    } catch (err) {
      console.error('Error during fetchUserAccessorials: ', err.message);
    }
  };

  const setDocumentFile = (file, docType) => {
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      const newCurrentDocs = [...currentDocs, file];
      setCurrentDocs(newCurrentDocs);
    } else {
      const mappedDocuments = requiredDocs.map((requiredDoc) => {
        if (requiredDoc.value !== docType) return requiredDoc;
        return {
          ...(FEATURE_FLAG_ENABLED_BREADCRUMB && requiredDoc),
          value: docType,
          file,
        };
      });
      setRequiredDocs(mappedDocuments);
    }
  };

  const onFileUpload = (_foreignKeys, type, uploadedFile) => {
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      const file = uploadedFile[0]; // can only upload 1 file at a time so the uploadedFile array only has 1 item
      file.foreignKeys = _foreignKeys;
      setFiles([...files, file]);
      setDocumentFile(file, type);
    } else {
      const mappedFile = { ...uploadedFile, type };
      setFiles([...files, mappedFile]);
      setDocumentFile(mappedFile, type);
    }
  };

  const filterHiddenFromCarrierDocs = (docList) => {
    return docList.filter((doc) => !hiddenFromCarrierMap[doc?.value]);
  };

  const renderRequiredAccessorialDocuments = () => {
    const userCurrentDocs = filterHiddenFromCarrierDocs(currentDocs);
    if ((requiredDocs?.length ?? 0) === 0 && !FEATURE_FLAG_ENABLED_BREADCRUMB) {
      return (
        <p data-testid="p-d1e2d334" className="mt-2">
          {t('NO_DOCUMENTS_REQUIRED_FOR_SPECIFIC_ACCESSORIAL_REQUEST')}
        </p>
      );
    }

    return (
      <RequiredAccessorialDocumentList
        currentDocs={userCurrentDocs}
        requiredDocs={requiredDocs}
        uploadedFiles={files}
        onUpload={onFileUpload}
        shipment={shipment}
        handleDocumentUploading={handleDocumentUploading}
        accessorial={accessorial}
        isExistingAccessorialRequest={isExistingAccessorialRequest}
        accessorialTypeId={accessorialTypeId}
      />
    );
  };

  return (
    <div data-testid={dataTestId} className={containerClassName}>
      <Label
        dataTestId="Label-1bb78094"
        className="mt-4 mb-2"
        helptext={t('REQUIRED_ACCESSORIAL_DOCUMENT_TOOLTIP')}
      >
        {FEATURE_FLAG_ENABLED_BREADCRUMB && hasGenericSupportingDocOnly
          ? t('PLEASE_PROVIDE_SUPPORTING_DOCUMENTATION_FOR_THIS_ACCESSORIAL')
          : t('PLEASE_PROVIDE_THE_FOLLOWING_DOCUMENTS')}
      </Label>
      {isLoading ? renderSpinner() : renderRequiredAccessorialDocuments()}
    </div>
  );
};

ViewAccessorialRequestDocumentRequirements.propTypes = {
  containerClassName: PropTypes.string,
  accessorial: PropTypes.object.isRequired,
  shipper: PropTypes.string.isRequired,
  shipment: PropTypes.object.isRequired,
  handleDocumentUploading: PropTypes.func,
  handleCustomerApprovalChanges: PropTypes.func,
  handleUpdateAccessorialRequiredDocs: PropTypes.func,
};

ViewAccessorialRequestDocumentRequirements.defaultProps = {
  handleDocumentUploading: () => { },
  handleCustomerApprovalChanges: () => { },
  handleUpdateAccessorialRequiredDocs: () => { },
  setIsCarrierMissingRequiredAccDocuments: () => { },
  hasGenericSupportingDocOnly: false,
  setHasGenericSupportingDocOnly: () => { },
  isExistingAccessorialRequest: false,
};

export default ViewAccessorialRequestDocumentRequirements;
